import type { Probe, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/**
 * Micro-task ordering under main-thread pressure (novel_bot_detection §2B).
 *
 * Stealth browsers inject JS before page load
 * (Page.addScriptToEvaluateOnNewDocument) to proxy native methods such as
 * HTMLCanvasElement.prototype.toDataURL or the AudioContext constructor. Even
 * when the wrapper returns a correct spoofed value, the extra JS execution
 * frames shift micro-task tick counts and ordering relative to the native C++
 * bindings.
 *
 * Method: run an IDENTICAL task schedule twice and compare the resulting
 * ordering. A native engine is deterministic here; a heavily proxied one can
 * interleave differently because each wrapper adds frames. Both passes MUST run
 * the exact same work — an earlier revision probed native methods in only one
 * pass, which made the comparison meaningless.
 *
 * We also record whether the MICRO-TASK portion matches the event-loop spec for
 * this schedule: queueMicrotask, then promise reaction, then MutationObserver
 * callback, in enqueue order. Verified in Chromium, Firefox and WebKit. We
 * deliberately do NOT assert the MACRO-task order (MessageChannel vs
 * setTimeout): WebKit runs them the other way round, which is spec-legal.
 * Every ordering value is reported as a signal and none is scored.
 */

const EXPECTED_MICRO_ORDER = ['queueMicrotask', 'promise', 'mutationObserver'];
const ORDERING_SOURCE = 'https://html.spec.whatwg.org/multipage/webappapis.html#event-loop-processing-model';

export const engineTimingProbe: Probe = {
  id: 'engineTiming',
  label: 'Engine micro-task timing',
  layer: 'engine',
  tier: 'client',
  category: 'browser',
  description: 'Micro-task ordering and native-call scheduling under load',
  sources: [ORDERING_SOURCE],
  budgetMs: 2000,

  async run(): Promise<ProbeResult> {
    const first = await runOrderingPass();
    const second = await runOrderingPass();

    const shape = first.order.map((o) => o.split(':')[0]);
    const shape2 = second.order.map((o) => o.split(':')[0]);
    const stable = shape.join('>') === shape2.join('>');

    // Commonly-proxied native methods, checked independently of the ordering.
    const natives = probeNatives();

    return {
      signals: {
        firstPassOrder: ok(first.order, 'client'),
        secondPassOrder: ok(second.order, 'client'),
        observedTaskTypes: ok([...new Set(shape)].sort(), 'derived'),
        microOrder: ok(shape.filter((t) => EXPECTED_MICRO_ORDER.includes(t)).join('>'), 'derived'),
        // The micro-task portion must match the enqueue order the HTML event
        // loop prescribes. Verified in Chromium, Firefox and WebKit.
        microOrderMatchesSpec: ok(microOrderMatchesSpec(shape), 'derived'),
        // Whether microtasks all precede macrotasks (the spec-expected shape).
        // Recorded for cross-engine comparison; not scored on its own.
        followsCanonicalShape: ok(matchesCanonicalShape(shape), 'derived'),
        orderingStable: ok(stable, 'derived'),
        canvasNativeOk: ok(natives.canvasOk, 'client'),
        webglNativeOk: ok(natives.webglOk, 'client'),
      },
      parts: {
        engineTiming: {
          // Only the SET of task types the engine supports. This is a genuine
          // structural property of the engine and is identical across runs.
          //
          // `orderingStable` is deliberately EXCLUDED: it is a detection signal
          // (a proxied environment may interleave differently), not device
          // identity, and a single unlucky interleaving would flip the part
          // between runs in the same browser — breaking the compare view.
          // Performance throughput and audio jitter are excluded for the same
          // reason: they track machine load, not identity.
          observedTaskTypes: [...new Set(shape)].sort(),
          followsCanonicalShape: matchesCanonicalShape(shape),
        },
      },
    };
  },
};

/**
 * One IDENTICAL scheduling pass. The first and second pass both call this, so
 * the only variable between them is the engine's own behaviour.
 */
async function runOrderingPass(): Promise<{ order: string[] }> {
  const order: string[] = [];
  let tick = 0;

  const host = document.createElement('div');
  host.style.display = 'none';
  document.body.appendChild(host);

  const pending: Promise<void>[] = [];

  order.push(`sync:${tick++}`);

  queueMicrotask(() => {
    order.push(`queueMicrotask:${tick++}`);
  });

  pending.push(
    Promise.resolve().then(() => {
      order.push(`promise:${tick++}`);
    }),
  );

  const mo = new MutationObserver(() => {
    order.push(`mutationObserver:${tick++}`);
  });
  mo.observe(host, { childList: true });

  const mc = new MessageChannel();
  pending.push(
    new Promise<void>((resolve) => {
      mc.port1.onmessage = () => {
        order.push(`messageChannel:${tick++}`);
        resolve();
      };
      mc.port2.postMessage(null);
    }),
  );

  host.appendChild(document.createElement('span'));

  pending.push(
    new Promise<void>((resolve) => {
      setTimeout(() => {
        order.push(`timeout:${tick++}`);
        resolve();
      }, 0);
    }),
  );

  await Promise.allSettled(pending);
  mo.disconnect();
  host.remove();
  return { order };
}

/** Exercises two commonly-proxied native methods. */
function probeNatives(): { canvasOk: boolean; webglOk: boolean } {
  let canvasOk = false;
  try {
    const c = document.createElement('canvas');
    c.width = 4;
    c.height = 4;
    c.getContext('2d');
    canvasOk = c.toDataURL().length > 0;
  } catch {
    canvasOk = false;
  }

  let webglOk = false;
  try {
    const gl = document.createElement('canvas').getContext('webgl') as WebGLRenderingContext | null;
    webglOk = !!gl && typeof gl.getParameter(gl.RENDERER) === 'string';
  } catch {
    webglOk = false;
  }

  return { canvasOk, webglOk };
}

/** True when all observed microtasks precede all observed macrotasks. */
function matchesCanonicalShape(shape: string[]): boolean {
  const microIdx = shape
    .map((t, i) => (['queueMicrotask', 'promise', 'mutationObserver'].includes(t) ? i : -1))
    .filter((i) => i >= 0);
  const macroIdx = shape
    .map((t, i) => (['messageChannel', 'timeout'].includes(t) ? i : -1))
    .filter((i) => i >= 0);
  if (microIdx.length === 0 || macroIdx.length === 0) return false;
  return Math.max(...microIdx) < Math.min(...macroIdx);
}

/**
 * True when the micro-task portion appears exactly in the enqueue order the
 * HTML event loop prescribes for this schedule:
 * queueMicrotask → promise reaction → MutationObserver callback.
 * (Macro-task order is deliberately not checked.)
 */
function microOrderMatchesSpec(shape: string[]): boolean {
  const micro = shape.filter((t) => EXPECTED_MICRO_ORDER.includes(t));
  return (
    micro.length === EXPECTED_MICRO_ORDER.length &&
    micro.every((t, i) => t === EXPECTED_MICRO_ORDER[i])
  );
}

registerProbe(engineTimingProbe);
